import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, CheckCircle2, Loader2, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { useControlledDocument, useAcknowledgeDocument } from '@/hooks/use-documents';
import { toast } from '@/app/components/ui/simple-toaster';
import { resolveMediaUrl } from '@/lib/api-client';

export function AcknowledgePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const docId = Number(id);

  const { data: doc, isLoading, error } = useControlledDocument(docId);
  const acknowledgeDocument = useAcknowledgeDocument();

  const handleAcknowledge = async () => {
    try {
      await acknowledgeDocument.mutateAsync(docId);
      toast.success('You have acknowledged reading this document.');
      navigate(`/compliance/documents/${docId}`);
    } catch {
      toast.error('Failed to acknowledge document. You may have already acknowledged this version.');
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Failed to load document.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/compliance/documents/${docId}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Read & Acknowledge</h1>
          <p className="text-gray-600 mt-1">Review the document and confirm acknowledgment</p>
        </div>
      </div>

      {/* Document Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {doc.title}
              </CardTitle>
              <CardDescription className="mt-1">
                {doc.document_number && <span className="font-mono">{doc.document_number} · </span>}
                Version {doc.current_version} · {doc.category.charAt(0).toUpperCase() + doc.category.slice(1)}
              </CardDescription>
            </div>
            <Badge variant="default">v{doc.current_version}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {doc.description && (
            <p className="text-sm text-gray-600">{doc.description}</p>
          )}

          {doc.related_clauses.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Related QP-300 Clauses</h4>
              <div className="flex flex-wrap gap-2">
                {doc.related_clauses.map((clause) => (
                  <Badge key={clause} variant="outline">
                    Clause {clause}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <a href={resolveMediaUrl(doc.file)} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Download & Read Document
              </Button>
            </a>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Please download and read the full document before acknowledging.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Acknowledgment */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-blue-500 mx-auto" />
            <div>
              <h3 className="font-medium text-gray-900">Confirm Acknowledgment</h3>
              <p className="text-sm text-gray-600 mt-1">
                By clicking below, you confirm that you have read and understood{' '}
                <strong>{doc.title}</strong> (v{doc.current_version}).
              </p>
            </div>
            <Button
              size="lg"
              className="w-full sm:w-auto"
              onClick={handleAcknowledge}
              disabled={acknowledgeDocument.isPending}
            >
              {acknowledgeDocument.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              I Have Read & Acknowledge This Document
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
